// Help Center content, static in-code knowledge base.
//
// Structure:
//   categories: [{ slug, name, icon, summary, articles: [Article] }]
//   Article:    { slug, title, description, keywords?, body: [Block] }
//   Block:      one of
//     { type: "p", text }
//     { type: "h", text }
//     { type: "steps", items: [string] }         ordered, numbered
//     { type: "list", items: [string] }          unordered, bulleted
//     { type: "callout", variant, text }          variant: "info" | "tip" | "warning"
//     { type: "faq", items: [{ q, a }] }
//
// Rendering lives in HelpArticle.jsx / helpBlocks.jsx. Keep this file plain data.

export const DOWNLOAD_LINKS = {
  android: "https://play.google.com/store/apps/details?id=ai.zerotyping",
  ios: "https://apps.apple.com/us/app/zero-typing-ai-voice-to-text/id6752406283",
  windows: "https://zerotyping.ai/ztaupdate/ZeroTypingAI.msi",
  macos: "https://zerotyping.ai/ztaupdate/ZeroTypingAI.dmg",
  webapp: "/app",
};

export const helpCategories = [
  // ─────────────────────────────────────────────────────────────
  {
    slug: "getting-started",
    name: "Getting Started",
    summary: "What Zero Typing is, how it works, and how to start dictating in minutes.",
    articles: [
      {
        slug: "what-is-zero-typing",
        title: "What is Zero Typing?",
        description: "Speak anywhere instead of typing, and get polished text ready to paste.",
        keywords: ["overview", "intro", "about", "how it works"],
        body: [
          { type: "p", text: "Zero Typing lets you speak instead of type, anywhere on your device. It captures your voice, transcribes it in real time, and can run the result through AI to clean up grammar, reformat it, or translate it, then drops ready-to-use text wherever your cursor is." },
          { type: "h", text: "What you can do" },
          { type: "list", items: [
            "Dictate into almost any app, including email, docs, chat, notes, and browser fields.",
            "Transcribe your speech word for word in any app, or translate into English in the Web App beta.",
            "Apply AI prompts to fix grammar, rephrase, or format text professionally.",
            "Teach it your own vocabulary, like names, brands, and technical terms, with a personal dictionary.",
          ]},
          { type: "h", text: "Where it runs" },
          { type: "list", items: [
            "Android: system-wide dictation via an on-screen mic button, live on Google Play.",
            "Windows: a desktop app with a global hotkey, available to download.",
            "Web App: dictate right in your browser at zerotyping.ai/app.",
            "iOS and macOS: coming soon.",
          ]},
          { type: "callout", variant: "tip", text: "New here? Start with How dictation works below, then jump to the setup guide for your device." },
        ],
      },
      {
        slug: "how-dictation-works",
        title: "How dictation works",
        description: "The three steps behind every dictation: speak, process, and paste.",
        keywords: ["transcribe", "translate", "prompt", "pipeline", "flow"],
        body: [
          { type: "p", text: "Every dictation follows the same simple flow, no matter which platform you are on." },
          { type: "steps", items: [
            "Speak. Tap the mic or press your hotkey and talk naturally. Your voice is streamed and transcribed live.",
            "Process. Zero Typing turns speech into text. Depending on your mode, it transcribes word for word, translates into English, or runs your text through an AI prompt.",
            "Paste. The finished text is placed where your cursor is, or copied so you can paste it anywhere.",
          ]},
          { type: "h", text: "Choosing a mode" },
          { type: "list", items: [
            "Transcribe converts your speech to text exactly as spoken.",
            "Translate lets you speak in another language and get clean English text. This is currently a Web App beta feature.",
            "AI prompt applies a template, such as Fix grammar or Make it professional, to polish the result.",
          ]},
          { type: "callout", variant: "info", text: "Speak in complete thoughts and pause naturally. You do not need to say punctuation, because smart formatting adds it for you." },
        ],
      },
      {
        slug: "choosing-your-platform",
        title: "Which app should I install?",
        description: "Pick the right Zero Typing for your device.",
        keywords: ["download", "install", "platform", "which app"],
        body: [
          { type: "p", text: "Zero Typing works across your devices. Here is how to choose." },
          { type: "list", items: [
            "On a phone or tablet that runs Android, install from Google Play for system-wide dictation.",
            "On a Windows PC, download the desktop installer and dictate with a global hotkey.",
            "On any computer right now, open the Web App in your browser with nothing to install.",
            "On iPhone, iPad, or Mac, these apps are coming soon.",
          ]},
          { type: "callout", variant: "tip", text: "You sign in with the same account everywhere, so your prompts, dictionary, and plan follow you across devices." },
        ],
      },
      {
        slug: "create-account-sign-in",
        title: "Creating an account and signing in",
        description: "Sign in with Google or Microsoft to sync your settings.",
        keywords: ["login", "sign up", "account", "google", "microsoft", "apple"],
        body: [
          { type: "p", text: "Your account keeps your prompts, personal dictionary, usage, and subscription in sync across every device." },
          { type: "steps", items: [
            "Open Zero Typing, either the app or the Web App, and choose Sign in.",
            "Pick Google, Microsoft, or Apple and complete the sign-in with that provider.",
            "You are returned to Zero Typing, signed in and ready to dictate.",
          ]},
          { type: "callout", variant: "info", text: "There is no separate password to remember, because you sign in through Google, Microsoft, or Apple. Use the same provider each time to stay on the same account." },
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  {
    slug: "android",
    name: "Android",
    summary: "Install, grant permissions, and dictate system-wide on any Android app.",
    articles: [
      {
        slug: "install-android",
        title: "Install Zero Typing on Android",
        description: "Get the app from Google Play and sign in.",
        keywords: ["download", "play store", "install", "android"],
        body: [
          { type: "steps", items: [
            "[Download Zero Typing from the Google Play Store](https://play.google.com/store/apps/details?id=ai.zerotyping), or search for Zero Typing in the Play Store app.",
            "Tap Install and wait for the download to finish.",
            "Open the app and sign in with Google or Microsoft.",
            "Follow the on-screen onboarding to grant the permissions Zero Typing needs.",
          ]},
        ],
      },
      {
        slug: "android-permissions",
        title: "Granting Android permissions",
        description: "Microphone, Accessibility, Display over other apps, and Notifications.",
        keywords: ["permission", "accessibility", "overlay", "microphone", "notification"],
        body: [
          { type: "p", text: "System-wide dictation relies on a few Android permissions. Zero Typing walks you through each one during onboarding, and you can manage them any time from Settings > Manage Permissions in the app." },
          { type: "h", text: "The permissions and why they are needed" },
          { type: "list", items: [
            "Microphone lets Zero Typing hear and transcribe your voice.",
            "Accessibility Service lets Zero Typing insert text into other apps and operate in the background.",
            "Display over other apps shows the floating mic button on top of whatever you are using.",
            "Notifications, which are optional, let you toggle the overlay quickly from the notification shade.",
          ]},
          { type: "callout", variant: "warning", text: "If dictation is not inserting text into other apps, the Accessibility Service is almost always the permission to check first." },
        ],
      },
      {
        slug: "android-dictate-anywhere",
        title: "Dictating in any app",
        description: "Use the floating mic button to speak into any text field.",
        keywords: ["overlay", "mic button", "dictate", "keyboard"],
        body: [
          { type: "steps", items: [
            "Open any app with a text field and tap where you want to type.",
            "Tap the floating Zero Typing mic button on screen.",
            "Speak naturally. Your words appear as text in the field.",
            "Tap the button again to stop. Your finished text stays in place.",
          ]},
          { type: "callout", variant: "tip", text: "Cannot see the floating button? Make sure Display over other apps is enabled, then turn the overlay on from the app or your notification shade." },
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  {
    slug: "windows",
    name: "Windows",
    summary: "Install the desktop app and dictate anywhere with a global hotkey.",
    articles: [
      {
        slug: "install-windows",
        title: "Install Zero Typing on Windows",
        description: "Download and run the installer, then sign in.",
        keywords: ["download", "installer", "msi", "windows", "setup"],
        body: [
          { type: "steps", items: [
            "[Download the Windows installer](https://zerotyping.ai/ztaupdate/ZeroTypingAI.msi), a file named ZeroTypingAI.msi.",
            "Open the downloaded file and follow the installer prompts.",
            "Launch Zero Typing. It runs in the background and lives in your system tray.",
            "Sign in with Google, Microsoft, or Apple to get started.",
          ]},
          { type: "callout", variant: "info", text: "If Windows shows a SmartScreen prompt, choose More info > Run anyway to continue." },
        ],
      },
      {
        slug: "windows-hotkey",
        title: "Using the dictation hotkey",
        description: "Press your shortcut to start and stop dictation anywhere.",
        keywords: ["hotkey", "shortcut", "keyboard", "tray", "dictate"],
        body: [
          { type: "p", text: "On Windows, Zero Typing runs quietly in the background. You trigger dictation with a keyboard shortcut, so you never have to switch windows." },
          { type: "steps", items: [
            "Click into any text field, such as a document, email, chat box, or browser field.",
            "Press your Zero Typing hotkey to start recording.",
            "Speak naturally, then press the hotkey again to stop.",
            "Your finished text is inserted at the cursor.",
          ]},
          { type: "callout", variant: "tip", text: "You can view or change the shortcut in the app settings. If a shortcut clashes with another program, pick a different key combination." },
        ],
      },
      {
        slug: "windows-tray",
        title: "The system tray and background app",
        description: "Find Zero Typing, open settings, and quit from the tray.",
        keywords: ["tray", "background", "quit", "settings", "notification area"],
        body: [
          { type: "p", text: "Zero Typing sits in the Windows system tray, the notification area near the clock, so it is always ready without cluttering your taskbar." },
          { type: "list", items: [
            "Click the tray icon to open the Zero Typing window and settings.",
            "Right-click for quick options like opening settings or quitting the app.",
            "If you do not see the icon, click the Show hidden icons arrow in the tray.",
          ]},
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  {
    slug: "macos",
    name: "macOS",
    badge: "Coming soon",
    summary: "Menu-bar dictation with a global hotkey.",
    articles: [
      {
        slug: "install-macos",
        title: "Install Zero Typing on macOS",
        description: "Download the app and grant the permissions it needs.",
        keywords: ["download", "dmg", "mac", "install", "menu bar"],
        body: [
          { type: "callout", variant: "info", text: "The macOS app is coming soon. This guide covers how setup works so you are ready at launch." },
          { type: "steps", items: [
            "Download the macOS app, named ZeroTypingAI.dmg, once it is available.",
            "Open the .dmg and drag Zero Typing into your Applications folder.",
            "Launch it. Zero Typing lives in your menu bar at the top of the screen.",
            "Sign in with Google, Microsoft, or Apple.",
          ]},
        ],
      },
      {
        slug: "macos-permissions",
        title: "Accessibility and Microphone permissions",
        description: "Grant the two macOS permissions Zero Typing needs to work.",
        keywords: ["accessibility", "microphone", "privacy", "system settings", "permission"],
        body: [
          { type: "p", text: "macOS requires two permissions for system-wide dictation. Zero Typing prompts you for both on first launch." },
          { type: "h", text: "Microphone" },
          { type: "p", text: "Allow microphone access when prompted so Zero Typing can hear you. You can manage it later under System Settings > Privacy and Security > Microphone." },
          { type: "h", text: "Accessibility" },
          { type: "steps", items: [
            "Open System Settings > Privacy and Security > Accessibility.",
            "Find Zero Typing in the list and turn the switch on.",
            "If prompted, enter your password to confirm the change.",
          ]},
          { type: "callout", variant: "warning", text: "Without Accessibility permission, Zero Typing cannot insert text into other apps or use its global hotkey. If dictation stops working after an update, re-check this toggle." },
        ],
      },
      {
        slug: "macos-hotkey",
        title: "Dictating with the menu-bar hotkey",
        description: "Use your shortcut to dictate into any macOS app.",
        keywords: ["hotkey", "fn", "shortcut", "menu bar", "dictate"],
        body: [
          { type: "p", text: "Zero Typing lives in the menu bar and is triggered by a keyboard shortcut, so you can dictate without leaving the app you are in. By default the shortcut uses the Fn key, and you can change it in settings." },
          { type: "steps", items: [
            "Click into any text field in any app.",
            "Press your Zero Typing hotkey to start recording.",
            "Speak naturally, then press it again to stop.",
            "Your finished text is inserted at the cursor.",
          ]},
          { type: "callout", variant: "tip", text: "Prefer a different trigger? Open Zero Typing from the menu bar and set a custom shortcut in settings." },
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  {
    slug: "ios",
    name: "iOS",
    badge: "Coming soon",
    summary: "Dictate anywhere with the Zero Typing keyboard.",
    articles: [
      {
        slug: "install-ios",
        title: "Install Zero Typing on iPhone and iPad",
        description: "Get the app from the App Store and sign in.",
        keywords: ["download", "app store", "iphone", "ipad", "install"],
        body: [
          { type: "callout", variant: "info", text: "The iOS app is coming soon to the App Store. This guide covers how it works so you are ready at launch." },
          { type: "steps", items: [
            "Open the App Store and search for Zero Typing.",
            "Tap Get to install, then open the app.",
            "Sign in with Google, Microsoft, or Apple.",
            "Follow the onboarding to enable the Zero Typing keyboard.",
          ]},
        ],
      },
      {
        slug: "ios-enable-keyboard",
        title: "Enabling the Zero Typing keyboard",
        description: "Add the keyboard and allow Full Access so you can dictate.",
        keywords: ["keyboard", "full access", "enable", "globe", "settings"],
        body: [
          { type: "p", text: "On iOS, Zero Typing works as a custom keyboard. You add it once, then switch to it in any app to dictate." },
          { type: "steps", items: [
            "Open iOS Settings > General > Keyboard > Keyboards > Add New Keyboard.",
            "Choose Zero Typing from the list.",
            "Tap Zero Typing again and turn on Allow Full Access, which is required for dictation to work.",
            "In any app, tap and hold the globe key and switch to the Zero Typing keyboard.",
          ]},
          { type: "callout", variant: "warning", text: "Full Access is required so the keyboard can record your voice and process it. Without it, dictation cannot run." },
        ],
      },
      {
        slug: "ios-dictate",
        title: "Dictating with the keyboard",
        description: "Speak into any text field on iPhone or iPad.",
        keywords: ["dictate", "keyboard", "microphone", "speak"],
        body: [
          { type: "steps", items: [
            "Tap into any text field and switch to the Zero Typing keyboard using the globe key.",
            "Tap the mic and allow microphone access the first time.",
            "Speak naturally, and your words are transcribed into the field.",
            "Tap again to stop. Your finished text stays in place.",
          ]},
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  {
    slug: "web-app",
    name: "Web App",
    summary: "Dictate right in your browser, with nothing to install.",
    articles: [
      {
        slug: "using-web-app",
        title: "Using the Web App",
        description: "Open zerotyping.ai/app and start dictating in your browser.",
        keywords: ["web", "browser", "app", "dictate", "online"],
        body: [
          { type: "p", text: "The Web App lets you dictate straight from your browser, which is perfect for trying Zero Typing or using it on a computer where you cannot install software." },
          { type: "steps", items: [
            "[Open the Web App](/app) and sign in.",
            "Allow microphone access when your browser asks.",
            "Tap the mic to start, speak naturally, and tap again to stop.",
            "Copy your finished text and paste it wherever you need it.",
          ]},
          { type: "callout", variant: "tip", text: "For the best experience, use a modern browser like Chrome or Edge and a good microphone in a quiet space." },
        ],
      },
      {
        slug: "web-app-install-pwa",
        title: "Installing the Web App as a PWA",
        description: "Add Zero Typing to your desktop or home screen for quick access.",
        keywords: ["pwa", "install", "add to home screen", "desktop"],
        body: [
          { type: "p", text: "The Web App can be installed like a regular app, so it opens in its own window and is one tap away." },
          { type: "list", items: [
            "On desktop Chrome or Edge, click the install icon in the address bar, or open the browser menu and choose Install Zero Typing.",
            "On mobile, open your browser Share or menu and choose Add to Home Screen.",
          ]},
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  {
    slug: "prompts-and-ai",
    name: "Prompts and AI",
    summary: "Transcribe, translate, apply custom prompts, and use smart formatting.",
    articles: [
      {
        slug: "transcribe-vs-translate",
        title: "Transcribe vs. Translate",
        description: "Get word-for-word text, or speak another language and get English.",
        keywords: ["transcribe", "translate", "language", "english", "mode"],
        body: [
          { type: "list", items: [
            "Transcribe converts your speech to text in the language you spoke.",
            "Translate lets you speak in another language, and Zero Typing returns clean English text.",
          ]},
          { type: "callout", variant: "info", text: "Translation is currently available in the [Web App](/app) only, as a beta feature, and produces English output. It is not yet released in the Android, Windows, iOS, or macOS apps." },
        ],
      },
      {
        slug: "custom-prompts",
        title: "Creating and using custom prompts",
        description: "Turn raw speech into polished, formatted text automatically.",
        keywords: ["prompt", "gpt", "ai", "grammar", "rephrase", "format", "template"],
        body: [
          { type: "p", text: "Prompts are instructions Zero Typing applies to your dictation with AI, such as Fix grammar and punctuation, Make this sound professional, or Format as a bulleted list. Instead of raw transcription, you get text that is ready to send." },
          { type: "h", text: "Creating and managing prompts" },
          { type: "p", text: "The Prompts screen in the app is where you manage your prompts. From here you can create a new prompt, set one as your default, or turn prompts on and off so only the ones you want appear when you dictate." },
          { type: "steps", items: [
            "Open the Prompts screen in the app.",
            "Choose to add a new prompt, give it a name, and write the instruction in plain language.",
            "Save it. Set it as your default, or toggle prompts off to hide the ones you do not need.",
          ]},
          { type: "h", text: "Choosing a prompt for your next dictation" },
          { type: "p", text: "You do not need to open the Prompts screen every time. Pick the prompt right before you dictate." },
          { type: "list", items: [
            "On Android, Windows, and macOS, open the prompt dropdown from the Zero Typing tray or overlay and pick the prompt for your next dictation. Long-press a prompt in the dropdown to make it your default.",
            "On iOS, choose your prompt from inside the Zero Typing keyboard rather than a dropdown.",
          ]},
          { type: "callout", variant: "tip", text: "Set a default prompt for the style you use most, and keep a plain Transcribe only option for when you want your exact words." },
        ],
      },
      {
        slug: "smart-formatting",
        title: "Smart formatting",
        description: "Automatic punctuation, capitalization, and clean paragraphs.",
        keywords: ["formatting", "punctuation", "capitalization", "smart"],
        body: [
          { type: "p", text: "Smart formatting cleans up your dictation automatically. It adds punctuation, capitalizes sentences, and breaks text into readable paragraphs, so you do not have to speak commas and periods out loud." },
          { type: "callout", variant: "info", text: "Just speak naturally in full sentences. Pause between thoughts, and smart formatting handles the rest." },
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  {
    slug: "personal-dictionary",
    name: "Personal Dictionary",
    summary: "Teach Zero Typing your names, brands, and technical terms.",
    articles: [
      {
        slug: "using-personal-dictionary",
        title: "Using your personal dictionary",
        description: "Get names, acronyms, and jargon spelled correctly every time.",
        keywords: ["dictionary", "custom words", "spelling", "names", "vocabulary", "special words"],
        body: [
          { type: "p", text: "The personal dictionary teaches Zero Typing words it might otherwise get wrong, like people names, company and product names, acronyms, and industry terms, so they are spelled the way you want." },
          { type: "steps", items: [
            "Open the Personal Dictionary screen in the app.",
            "Add each word or phrase exactly as it should appear.",
            "Save. Your entries sync to your account and apply to future dictations.",
          ]},
          { type: "callout", variant: "tip", text: "Add tricky proper nouns and brand names first, because they are the words transcription most often mishears." },
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  {
    slug: "plans-billing",
    name: "Plans and Billing",
    summary: "Free vs Pro, upgrading, managing, and cancelling your subscription.",
    articles: [
      {
        slug: "free-vs-pro",
        title: "Free vs. Pro",
        description: "What is included in each plan.",
        keywords: ["free", "pro", "plan", "pricing", "limit", "minutes", "upgrade"],
        body: [
          { type: "h", text: "Free" },
          { type: "list", items: [
            "15 minutes of transcription per week.",
            "Resets automatically at the start of each weekly cycle.",
            "Great for trying Zero Typing and for light use.",
          ]},
          { type: "h", text: "Pro" },
          { type: "list", items: [
            "Unlimited transcription minutes.",
            "Ideal for daily and professional use.",
            "Billed monthly or annually, and the annual plan saves money.",
          ]},
          { type: "callout", variant: "info", text: "Reached your free limit? Transcription pauses until your weekly cycle resets, or you can upgrade to Pro for unlimited use right away." },
        ],
      },
      {
        slug: "upgrade-to-pro",
        title: "Upgrading to Pro",
        description: "Subscribe monthly or annually.",
        keywords: ["upgrade", "subscribe", "checkout", "payment", "pro", "billing cycle"],
        body: [
          { type: "steps", items: [
            "Open Pricing on the website, or the upgrade option in your app.",
            "Choose a monthly or annual billing cycle.",
            "Complete secure checkout with your payment details.",
            "Your account switches to Pro immediately, with unlimited minutes across all your devices.",
          ]},
          { type: "callout", variant: "tip", text: "The annual plan costs less per month than paying monthly. Your Pro status follows your account on every platform you sign in to." },
        ],
      },
      {
        slug: "manage-cancel-subscription",
        title: "Managing or cancelling your subscription",
        description: "Change your plan or cancel anytime, with no fees.",
        keywords: ["cancel", "manage", "downgrade", "refund", "billing", "subscription"],
        body: [
          { type: "p", text: "You are in control of your subscription and can change it whenever you like." },
          { type: "steps", items: [
            "Go to Settings, or Manage Subscription, on the website while signed in.",
            "Choose to upgrade, downgrade, or cancel.",
            "Confirm your change.",
          ]},
          { type: "callout", variant: "info", text: "There are no contracts and no cancellation fees. If you cancel, Pro stays active until the end of your current billing period, then your account returns to Free." },
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  {
    slug: "privacy-data",
    name: "Privacy and Data",
    summary: "How Zero Typing protects your voice and text.",
    articles: [
      {
        slug: "how-we-handle-data",
        title: "How your data is handled",
        description: "Your voice is encrypted in transit and never sold.",
        keywords: ["privacy", "security", "data", "encryption", "voice", "storage"],
        body: [
          { type: "list", items: [
            "Your voice data is encrypted while it is being processed.",
            "It is sent to our third-party AI service provider only to deliver transcription and text-processing features, and is never sold.",
            "It is not stored long term, and is not used to train AI models.",
            "We follow strict privacy practices to keep your information safe.",
          ]},
          { type: "callout", variant: "info", text: "For full details, see the Privacy Policy at zerotyping.ai/privacy-policy." },
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  {
    slug: "troubleshooting",
    name: "Troubleshooting and FAQ",
    summary: "Quick fixes for the most common issues, plus frequent questions.",
    articles: [
      {
        slug: "mic-not-working",
        title: "The microphone is not working",
        description: "Fix no audio, or nothing being transcribed.",
        keywords: ["microphone", "mic", "no audio", "not working", "permission", "silent"],
        body: [
          { type: "p", text: "If your speech is not being picked up, work through these in order." },
          { type: "steps", items: [
            "Confirm microphone permission is granted to Zero Typing in your device or browser settings.",
            "Check that the correct microphone is selected, and that it is not muted or in use by another app.",
            "Speak clearly and close to the mic in a reasonably quiet space.",
            "Restart the app, or reload the Web App, and try again.",
          ]},
          { type: "callout", variant: "tip", text: "On a headset? Make sure the headset mic, not a disconnected or Bluetooth device, is the active input." },
        ],
      },
      {
        slug: "text-not-inserting",
        title: "Text is not appearing in other apps",
        description: "Dictation records but nothing gets typed where you want it.",
        keywords: ["not inserting", "no text", "accessibility", "keyboard", "paste"],
        body: [
          { type: "list", items: [
            "On Android, make sure the Accessibility Service and Display over other apps are both enabled for Zero Typing.",
            "On macOS, check Accessibility permission under System Settings > Privacy and Security > Accessibility.",
            "On iOS, confirm the Zero Typing keyboard has Full Access and is the active keyboard.",
            "On Windows, click into a text field first, then trigger the hotkey so the text has somewhere to go.",
          ]},
          { type: "callout", variant: "warning", text: "After a system or app update, permissions can reset. Re-checking the relevant permission fixes most cases where text will not insert." },
        ],
      },
      {
        slug: "reached-limit",
        title: "I have hit my transcription limit",
        description: "What happens on the free plan, and how to keep going.",
        keywords: ["limit", "free", "minutes", "reached", "blocked", "usage"],
        body: [
          { type: "p", text: "On the Free plan you get 15 minutes of transcription per week. When you reach it, transcription pauses until your weekly cycle resets." },
          { type: "list", items: [
            "Wait for the weekly reset to get your free minutes back, or",
            "[Upgrade to Pro](/settings) for unlimited transcription right away.",
          ]},
        ],
      },
      {
        slug: "general-faq",
        title: "Frequently asked questions",
        description: "Quick answers to the questions we hear most.",
        keywords: ["faq", "questions", "languages", "apps", "privacy", "cancel"],
        body: [
          { type: "faq", items: [
            { q: "Will Zero Typing work in the apps I already use?", a: "Yes. Zero Typing works across almost every app that accepts text input, including email, Google Docs, WhatsApp, and CRM notes. No special integrations are needed, so just place your cursor and start talking." },
            { q: "Can I dictate in my own language?", a: "Yes, you can dictate in many languages. Translation into English is currently available in the Web App only, as a beta feature, and is not yet released in the mobile or desktop apps." },
            { q: "Is my data private and secure?", a: "Your voice data is encrypted during processing and is never sold or used to train AI models. It is shared with our AI processing provider, OpenAI, only to deliver transcription, translation, and text-processing features. See the Privacy Policy for full details." },
            { q: "What happens if I reach my transcription limit?", a: "On the Free plan you get 15 minutes per week. Once reached, transcription pauses until the next weekly reset. Pro removes the limit entirely." },
            { q: "Can I cancel or change my plan anytime?", a: "Yes. You can cancel, upgrade, or downgrade anytime from your account. There are no contracts and no cancellation fees, and access continues until the end of your billing period." },
          ]},
        ],
      },
    ],
  },
];

// ── Helpers ───────────────────────────────────────────────────

export const getCategory = (slug) =>
  helpCategories.find((c) => c.slug === slug) || null;

export const getArticle = (categorySlug, articleSlug) => {
  const category = getCategory(categorySlug);
  if (!category) return { category: null, article: null };
  const article = category.articles.find((a) => a.slug === articleSlug) || null;
  return { category, article };
};

// Flattened index for search, one entry per article.
export const searchIndex = helpCategories.flatMap((c) =>
  c.articles.map((a) => ({
    categorySlug: c.slug,
    categoryName: c.name,
    slug: a.slug,
    title: a.title,
    description: a.description,
    keywords: a.keywords || [],
  }))
);

export const searchArticles = (query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return [];
  return searchIndex
    .map((entry) => {
      const haystack = [
        entry.title,
        entry.description,
        entry.categoryName,
        entry.keywords.join(" "),
      ]
        .join(" ")
        .toLowerCase();
      const score = haystack.includes(q)
        ? entry.title.toLowerCase().includes(q)
          ? 2
          : 1
        : 0;
      return { entry, score };
    })
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((r) => r.entry)
    .slice(0, 8);
};
